<!--
  For a mapbox gl documentation see https://www.mapbox.com/maplibre-gl-js/api/

  TODO: Consider making it so zoom-in/out needs Ctrl by default, unless fullscreen mode is active
-->

<template>
  <div ref="map" class="map">
    <div v-if="state.loaded">
      <slot :map="state.map" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ScaleControl,
  type RequestTransformFunction,
  Map,
  type MapOptions,
  type PaddingOptions,
  type LngLat
} from 'maplibre-gl'

import { useI18n } from 'vue-i18n'

type RecursiveStringArray = string | RecursiveStringArray[]

const i18n = useI18n()

const props = withDefaults(
  defineProps<{
    options?: Partial<MapOptions>
    showScale?: boolean
    mapStyle?: string
  }>(),
  {
    options: () => ({}),
    showScale: true,
    mapStyle: 'https://tiles.riserapp.com/styles/klokantech-basic/style.json'
  }
)

const map = ref<HTMLElement | null>(null)

const state = reactive<{
  loaded: boolean
  map: Map | undefined
  maximize: boolean
  bearing: number
  is3D: boolean
  show3dHint: boolean
}>({
  loaded: false,
  map: undefined,
  maximize: false,
  bearing: 0,
  is3D: false,
  show3dHint: true
})

const emit = defineEmits([
  'onLoad',
  'moveend',
  'drag',
  'zoom',
  'pan',
  'move',
  'rotate',
  'pitch',
  'dblclick',
  'contextmenu'
])

watchEffect(() => {
  if (!state.map) return

  state.map.setStyle(props.mapStyle)
})

const createMap = () => {
  if (!map.value) return

  const transformRequest: RequestTransformFunction = (url, resourceType) => {
    if (!resourceType) {
      return { url }
    }

    // Do any other transforms you want
    return { url }
  }

  state.map = new Map({
    container: map.value,
    style: props.mapStyle,
    transformRequest,
    maxZoom: 19.5,
    maxPitch: 85,
    canvasContextAttributes: {
      preserveDrawingBuffer: true
    },
    ...props.options
  })

  state.map.on('load', () => {
    setSymbolLayerLanguage(i18n.locale.value.split('-')[0])
  })

  // This is a custom map move event that allows not interact with the map even when animating
  // it only fires a moveend instead of multipe moves
  const centerPoint = function (options: {
    zoom: number
    center: LngLat
    bearing: number
    pitch: number
    padding: PaddingOptions
  }) {
    if (!state.map) return

    const tr = state.map._getTransformForUpdate()

    if ('zoom' in options && tr.zoom !== +options.zoom) {
      tr.setZoom(+options.zoom)
    }

    if (options.center !== undefined) {
      tr.setCenter(options.center)
    }

    if ('bearing' in options && tr.bearing !== options.bearing) {
      tr.setBearing(+options.bearing)
    }

    if ('pitch' in options && tr.pitch !== options.pitch) {
      tr.setPitch(+options.pitch)
    }

    if (options.padding != null && !tr.isPaddingEqual(options.padding)) {
      tr.setPadding(options.padding)
    }

    state.map._applyUpdatedTransform(tr)

    return state.map.fire({ type: 'moveend' })
  }

  if (props.showScale) {
    state.map.addControl(new ScaleControl({}), 'bottom-right')
  } // TODO: Make sure this supports imperial units; add toggle based on user settings

  state.map.on(
    'load',
    function () {
      state.loaded = true
      emit('onLoad', state.map, centerPoint)
    }.bind(this)
  )
  state.map.on('moveend', () => {
    emit('moveend', state.map)
  })

  state.map.on('drag', (event) => {
    emit('drag', event)
  })

  state.map.on('zoom', (event) => {
    emit('zoom', event)
  })

  state.map.on('pan', (event) => {
    emit('pan', event)
  })

  state.map.on('move', (event) => {
    if (!state.map) return

    state.bearing = state.map.getBearing()

    emit('move', event)
  })

  state.map.on('rotate', (event) => {
    emit('rotate', event)
  })

  state.map.on('pitch', (event) => {
    emit('pitch', event)
  })

  state.map.on('dblclick', (event) => {
    emit('dblclick', event)
  })

  state.map.on('contextmenu', (event) => {
    emit('contextmenu', event)
  })
}

const setSymbolLayerLanguage = (localeCode: string) => {
  state.map?.getStyle().layers.forEach((layer) => {
    if (layer.type !== 'symbol') return

    const coalesceExpression: RecursiveStringArray = [
      'coalesce',
      ['get', 'name:' + localeCode],
      ['get', 'name:en'],
      ['get', 'name_en'],
      ['get', 'name:latin'],
      ['get', 'name_latin'],
      ['get', 'name']
    ]

    state.map?.setLayoutProperty(layer.id, 'text-field', coalesceExpression)
  })
}

onMounted(() => {
  createMap()
})
</script>

<style>
@import 'maplibre-gl/dist/maplibre-gl.css';

.maplibregl-ctrl-attrib {
  @apply !tw-mr-5 !tw-rounded-lg tw-text-xs;

  line-height: inherit !important;
}

.maplibregl-ctrl-attrib-button {
  @apply !tw-rounded-lg;
}

.maplibregl-ctrl-scale {
  @apply tw-pointer-events-none !tw-mr-5;
}
</style>
